import type { ActivityDetail } from '@painpal/contracts';
import { useMemo, type ReactNode } from 'react';
import { LoadingBlock } from '../../components/StateViews';
import { routeLabel } from './detailModel';
import { LoadCard } from './LoadCard';
import { RouteMap } from './RouteMap';
import { routeCoordinates, routeGeometry } from './routeGeo';
import { detailLayout, layoutNoun } from './runDetailModel';
import { useStreams } from './streams';
import { SummaryTiles } from './SummaryTiles';
import styles from './detail.module.css';

function MapBox({ children }: { children: ReactNode }) {
  return <div className={styles.mapBox}>{children}</div>;
}

function NoMap({ reason }: { reason: string }) {
  return <MapBox><span className={styles.noMap}>{reason}</span></MapBox>;
}

function StreamsMap({ activity, url }: { activity: ActivityDetail; url: string }) {
  const streams = useStreams(activity.id, url);
  const geometry = useMemo(
    () => (streams.data === undefined ? null : routeGeometry(routeCoordinates(streams.data))),
    [streams.data],
  );
  if (streams.isPending) return <MapBox><LoadingBlock label="LOADING ROUTE" /></MapBox>;
  if (geometry === null) return <NoMap reason={streams.isError ? 'ROUTE UNAVAILABLE' : `NO GPS · INDOOR OR PRIVATE ${layoutNoun(detailLayout(activity))}`} />;
  const label = routeLabel(activity);
  return (
    <div className={styles.route}>
      {label !== null && <span className={styles.routeLabel}>{label}</span>}
      <MapBox><RouteMap geometry={geometry} /></MapBox>
    </div>
  );
}

// An HR-only session shows its map only once the streams prove GPS; until then the Load card and tiles take the row.
export function HeroRow({ activity, gps }: { activity: ActivityDetail; gps: boolean | null }) {
  const hrOnly = detailLayout(activity) === 'hr';
  const map = !hrOnly || gps === true;
  return (
    <>
      {map && (
        <div className={styles.mapArea}>
          {activity.streamsUrl === null
            ? <NoMap reason="NO STREAM DATA FOR THIS ACTIVITY" />
            : <StreamsMap activity={activity} url={activity.streamsUrl} />}
        </div>
      )}
      <div className={styles.summary} data-wide={!map || undefined}>
        <LoadCard activity={activity} />
        <SummaryTiles activity={activity} gps={gps} />
      </div>
    </>
  );
}
